import React, { useState, useEffect } from "react";
import { Button, Card, Tabs, Typography, Collapse } from "antd";
import {
  DollarSign,
  Building2,
  Package,
  Info,
  BarChart3,
  Calendar,
} from "lucide-react";
import {
  ServiceMetricsOverview,
  PackingMetricsTab,
  StorageBufferTab,
  ServiceKPIsTab,
  DailyReportsTab,
} from "~/components/admin/service-metrics";
import { useSearchParams, Navigate, Link } from "react-router-dom";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

const { Title, Text } = Typography;
const { Panel } = Collapse;

export const ServiceMetricsScreen: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "overview"
  );

  // Only allow super admins to access this page
  if (user?.role !== Role.SUPER_ADMIN) {
    return <Navigate to="/" replace />;
  }

  // Update URL when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  // Sync URL params with tab state
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [activeTab, searchParams]);

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <DollarSign size={16} />
          Overview
        </span>
      ),
      children: <ServiceMetricsOverview />,
    },
    {
      key: "kpis",
      label: (
        <span className="flex items-center gap-2">
          <BarChart3 size={16} />
          KPIs
        </span>
      ),
      children: <ServiceKPIsTab />,
    },
    {
      key: "daily-reports",
      label: (
        <span className="flex items-center gap-2">
          <Calendar size={16} />
          Daily Reports
        </span>
      ),
      children: <DailyReportsTab />,
    },
    {
      key: "packing-metrics",
      label: (
        <span className="flex items-center gap-2">
          <Package size={16} />
          Packing Metrics
        </span>
      ),
      children: <PackingMetricsTab />,
    },
    {
      key: "storage-kpi",
      label: (
        <span className="flex items-center gap-2">
          <Building2 size={16} />
          Storage KPI
        </span>
      ),
      children: <StorageBufferTab />,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-[95%] mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex justify-between items-start">
          <div>
            <Title level={2} className="!mb-2 !text-gray-900">
              Service Metrics
            </Title>
            <Text className="text-gray-600 text-base">
              Configure and manage pricing models, rates, and service calculations
            </Text>
          </div>
          <Link to="/operations/inventory-templates">
            <Button type="default" icon={<Package size={16} />} className="flex items-center gap-1">
              Inventory Templates
            </Button>
          </Link>
        </div>

        {/* Information Panel - Collapsible */}
        <Card 
          className="border-gray-200 shadow-sm"
          bodyStyle={{ padding: 0 }}
        >
          <Collapse
            ghost
            defaultActiveKey={[]}
            expandIcon={({ isActive }) => (
              <Info 
                size={16} 
                className={`text-gray-500 transition-transform ${isActive ? 'rotate-180' : ''}`}
              />
            )}
          >
            <Panel 
              header={
                <span className="text-gray-700 font-medium">
                  Pricing Calculation Guidelines
                </span>
              } 
              key="info"
            >
              <div className="space-y-3 text-sm text-gray-600 pl-6">
                <div>
                  <strong className="text-gray-900">Collection + Delivery:</strong> Vessel price hourly +
                  staff quantity × price each (standard position is art handler at
                  $100 per hour), multiplied by travel time + site duration
                </div>
                <div>
                  <strong className="text-gray-900">Site Service:</strong> Same as collection + delivery but
                  with a minimum of $300 per art handler for first 2 hours, then
                  usual hourly rate
                </div>
                <div>
                  <strong className="text-gray-900">Warehouse + Fabrication:</strong> Calculated with add-on
                  services based on cubic footage or hourly rates
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <strong className="text-gray-900">Example:</strong> Chelsea job with 2 staff ($200) and van
                  ($90), travel time 0.5 hours, site time 45 minutes = 1.25 hours ×
                  $290 = $362.50
                </div>
              </div>
            </Panel>
          </Collapse>
        </Card>

        {/* Main Content */}
        <Card 
          className="border-gray-200 shadow-sm min-h-[600px]"
          bodyStyle={{ padding: 0 }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            size="large"
            items={tabItems}
            className="px-6 pt-4"
          />
        </Card>
      </div>
    </div>
  );
};
